import React, { Component, Fragment } from 'react'
import { ApolloConsumer, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { SendToEmailModal } from 'contrastBase/Modals'
import { SendActionMenu } from './send-action-menu'
import { EmailDomainsQuery } from '../../security/email-domains-query'
import { areVulnerabilitiesFromSameApplication } from '../shared'
import { FormattedMessage } from 'react-intl'
import { logError, logSuccess } from '../../toaster'
import { PureQueryOptions } from 'apollo-client'
import { onSelectBugtrackerOption } from './send-action'
import { ActionProps } from '../../../legacy-design-system/action/action'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { IconSend } from '@contrast-security-inc/design-system-foundations'

const sendVulnerabilitiesToEmailMutation = gql`
  mutation sendVulnerabilitiesToEmailByFilters(
    $organizationID: ID!
    $emailAddress: String!
    $filters: VulnerabilityFilterArg!
    $excludedVulnerabilities: [ID!]!
  ) {
    sendVulnerabilitiesToEmailByFilters(
      input: {
        organizationID: $organizationID
        emailAddress: $emailAddress
        filters: $filters
        excludedVulnerabilities: $excludedVulnerabilities
      }
    ) {
      _placeholder
    }
  }
`

interface State {
  readonly showEmailDialog: boolean
}

interface Props extends Pick<ActionProps, 'size'> {
  readonly selectedCount: number
  readonly organizationID: string
  readonly excludedVulnerabilities: string[]
  readonly filters: {
    readonly filterText: string
    readonly quickFilter: string
    readonly aggregations: Record<string, string[]>
  }
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly vulnerabilities: Array<{
    readonly id: string
    readonly application: {
      id: string
      isParentApp?: boolean
      parentApplicationId?: string
    }
  }>
  readonly refetchQueries: PureQueryOptions[]
  readonly showDisabled?: boolean
  readonly refresh: () => Promise<any>
  /*
   * if validateIfVulnerabilitiesAreFromSameApplication is true, we will validate that all vulnerabilities belong to
   * the same application, otherwise we will assume that all vulnerabilities belong to the same application.
   *
   * e.g.
   * In the organization grid we need to validate all the vulnerabilities.
   * In the vulnerabilities tab within the application grid, is not necessary to validate the vulnerabilities.
   *
   */
  readonly validateIfVulnerabilitiesAreFromSameApplication?: boolean
}

interface MutationResponse {
  readonly _placeholder: boolean
}

interface MutationVars {
  readonly excludedVulnerabilities: string[]
  readonly emailAddress: string
  readonly organizationID: string
  readonly filters: Record<string, string[]> & {
    readonly filterText: string
    readonly quickFilter: string
  }
  readonly additionalFilters?: { readonly [key: string]: any }
}

export class SendWithFilterAction extends Component<Props, State> {
  public state = {
    showEmailDialog: false
  }

  public render() {
    const {
      organizationID,
      filters: { aggregations, filterText, quickFilter },
      additionalFilters,
      selectedCount,
      excludedVulnerabilities,
      vulnerabilities,
      refresh,
      refetchQueries,
      showDisabled,
      validateIfVulnerabilitiesAreFromSameApplication,
      size
    } = this.props
    const { showEmailDialog } = this.state

    return (
      <ApolloConsumer>
        {(apolloClient) => (
          <FormattedMessage
            id="detail_vulnerabilities_successfullySentVulnerabilityEmail"
            values={{ count: vulnerabilities.length }}
            children={(successMessage) => (
              <FormattedMessage
                id="detail_vulnerabilities_errorSendingVulnerabilityEmail"
                values={{ count: vulnerabilities.length }}
                children={(errorMessage) =>
                  showDisabled ? (
                    <ActionButton
                      isDisabled
                      TooltipProps={{
                        body: (
                          <FormattedMessage id="title_vulnerabilities_insufficientSendPermissions" />
                        )
                      }}
                      IconButtonProps={{
                        Icon: IconSend,
                        size,
                        'aria-label': 'Send'
                      }}
                    />
                  ) : (
                    <Fragment>
                      <SendActionMenu
                        size={size}
                        onSelectEmailOption={() =>
                          this.setState({ showEmailDialog: true })
                        }
                        vulnerabilityCount={selectedCount}
                        vulnerabilitiesAreFromSameApplication={
                          validateIfVulnerabilitiesAreFromSameApplication
                            ? areVulnerabilitiesFromSameApplication(
                                vulnerabilities
                              )
                            : true
                        }
                        onSelectBugtrackerOption={() =>
                          onSelectBugtrackerOption(
                            vulnerabilities,
                            refetchQueries,
                            apolloClient,
                            refresh
                          )
                        }
                      />
                      <Mutation<MutationResponse, MutationVars>
                        mutation={sendVulnerabilitiesToEmailMutation}
                      >
                        {(sendVulnerabilities) => (
                          <EmailDomainsQuery organizationID={organizationID}>
                            {({ domains }) => (
                              <FormattedMessage
                                id="title_vulnerabilities_sendVulnerability"
                                values={{ count: vulnerabilities.length }}
                                children={(sendVulnerabilityMessage) => (
                                  <SendToEmailModal
                                    title={sendVulnerabilityMessage as any}
                                    sendItems={async (emailAddress) => {
                                      try {
                                        await sendVulnerabilities({
                                          variables: {
                                            emailAddress,
                                            organizationID,
                                            filters: Object.assign(
                                              {},
                                              aggregations,
                                              {
                                                filterText,
                                                quickFilter,
                                                ...additionalFilters
                                              }
                                            ),
                                            excludedVulnerabilities
                                          }
                                        })
                                        logSuccess(successMessage as any)
                                      } catch (error) {
                                        logError(errorMessage as any)
                                        return error
                                      }
                                    }}
                                    isOpen={showEmailDialog}
                                    onClose={() =>
                                      this.setState({ showEmailDialog: false })
                                    }
                                    permittedDomains={domains}
                                  />
                                )}
                              />
                            )}
                          </EmailDomainsQuery>
                        )}
                      </Mutation>
                    </Fragment>
                  )
                }
              />
            )}
          />
        )}
      </ApolloConsumer>
    )
  }
}
